import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Fade,
  alpha,
  useTheme,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import QuadrimestreSelector, { getQuadrimestreAtual, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import HelpButton from '../../components/saude/HelpButton';
import ClassificacaoBadge, { getClassificacaoColor } from '../../components/saude/ClassificacaoBadge';
import TabPanel from '../../components/saude/TabPanel';
import EquipeCard from '../../components/saude/EquipeCard';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { EIXO_GROUPS } from '../../data/eixo-groups';
import ProgressBarWithMetas from '../../components/saude/ProgressBarWithMetas';
import { buildEixoContext } from '../../utils/saude-context-text';

const SaudeEixo: React.FC = () => {
  const { eixo } = useParams<{ eixo: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [resumo, setResumo] = useState<any>(null);
  const [resumoPorEquipe, setResumoPorEquipe] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [equipeSearch, setEquipeSearch] = useState('');

  const qAtual = getQuadrimestreAtual();
  const [quadrimestre, setQuadrimestre] = useState(qAtual.quadrimestre);
  const [ano, setAno] = useState(qAtual.ano);
  const qParam = formatQuadrimestreParam(quadrimestre, ano);

  // If coming from EquipeCard with ?tab=equipes, start on equipes tab
  useEffect(() => {
    if (searchParams.get('tab') === 'equipes') setTabValue(1);
  }, [searchParams]);

  const handleQuadChange = (q: string, a: number) => { setQuadrimestre(q); setAno(a); };

  const fetchData = useCallback(async () => {
    if (!municipioAtivo?.id || !eixo) return;
    setLoading(true); setError(null);
    try {
      const [resumoData, equipeBreakdown] = await Promise.all([
        saudeService.getEixoResumo(eixo, municipioAtivo.id, undefined, qParam, unidadeSelecionada?.codunidade),
        saudeService.getEixoEquipes(eixo, municipioAtivo.id, qParam, unidadeSelecionada?.codunidade),
      ]);
      setResumo(resumoData);
      setResumoPorEquipe(equipeBreakdown);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados do eixo.');
    } finally { setLoading(false); }
  }, [municipioAtivo?.id, eixo, qParam, unidadeSelecionada?.codunidade]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Compute average percentuais across all equipes for radar overlay
  const mediaIndicadores = useMemo(() => {
    if (!resumoPorEquipe.length) return undefined;
    const first = resumoPorEquipe[0]?.indicadores;
    if (!first?.length) return undefined;
    return first.map((_: any, idx: number) => {
      const sum = resumoPorEquipe.reduce((s: number, eq: any) => s + (eq.indicadores?.[idx]?.percentual ?? 0), 0);
      return sum / resumoPorEquipe.length;
    });
  }, [resumoPorEquipe]);

  // Indicator nomes map from resumo
  const indicadoresNomes = useMemo(() => {
    const map: Record<string, string> = {};
    (resumo?.indicadores || []).forEach((ind: any) => { map[ind.indicador] = ind.nome; });
    return map;
  }, [resumo]);

  const eixoGroup = useMemo(() => EIXO_GROUPS.find(g => g.eixos.includes(eixo!)), [eixo]);
  const dadosContexto = useMemo(() => {
    if (!resumo) return undefined;
    return buildEixoContext(resumo, resumoPorEquipe);
  }, [resumo, resumoPorEquipe]);

  // Sorted equipes (worst first)
  const sortedEquipes = useMemo(() => {
    let filtered = [...resumoPorEquipe];
    if (equipeSearch) {
      const q = equipeSearch.toLowerCase();
      filtered = filtered.filter((eq: any) =>
        eq.equipe?.toLowerCase().includes(q) || eq.unidade?.toLowerCase().includes(q) || eq.ine?.includes(q)
      );
    }
    return filtered.sort((a: any, b: any) => (a.score ?? 0) - (b.score ?? 0));
  }, [resumoPorEquipe, equipeSearch]);

  const isTaxa = resumo?.tipo === 'taxa';

  if (!municipioAtivo) {
    return (<Box sx={{ p: 3 }}><Alert severity="warning">Selecione um municipio para visualizar os indicadores.</Alert></Box>);
  }
  if (loading) {
    return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>);
  }
  if (error) {
    return (<Box sx={{ p: 3 }}><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert><Button variant="outlined" onClick={() => navigate(-1)}>Voltar</Button></Box>);
  }
  if (!resumo) {
    return (<Box sx={{ p: 3 }}><Alert severity="info">Nenhum dado encontrado para este eixo.</Alert></Box>);
  }

  const indicators = resumo.indicadores || [];
  const radarData = indicators.map((ind: any) => ({
    indicador: ind.indicador,
    nome: ind.nome,
    percentual: ind.percentual ?? 0,
    fullMark: 100,
  }));

  return (
    <>
    <Fade in timeout={600}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/saude')}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) } }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="h4" fontWeight={700}>{resumo.nome}</Typography>
              <HelpButton eixo={eixo!} size={18} />
            </Box>
            {eixoGroup && <Typography variant="body2" color="text.secondary">{eixoGroup.label} - {eixoGroup.descricao}</Typography>}
          </Box>
          <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
        </Box>

        {/* Stat strip */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GroupsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Identificados</Typography>
                  <Typography variant="h5" fontWeight={700}>{resumo.total_identificados?.toLocaleString('pt-BR') ?? '-'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(getClassificacaoColor(resumo.classificacao), 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PersonIcon sx={{ color: getClassificacaoColor(resumo.classificacao), fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Score</Typography>
                  <Typography variant="h5" fontWeight={700}>{resumo.score_medio ?? '-'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <ClassificacaoBadge classificacao={resumo.classificacao} size="medium" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="fullWidth"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab label="Visao Geral" id="tab-0" aria-controls="tabpanel-0" />
            <Tab label={`Por Equipe (${resumoPorEquipe.length})`} id="tab-1" aria-controls="tabpanel-1" />
            {!isTaxa && <Tab label="Lista Nominal" id="tab-2" aria-controls="tabpanel-2" />}
          </Tabs>
        </Box>

        {/* Tab 0: Visão Geral */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Indicators */}
            <Grid item xs={12} md={radarData.length >= 3 && radarData.some((d: any) => d.percentual > 0) ? 7 : 12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Indicadores</Typography>
              <Grid container spacing={1.5}>
                {indicators.map((ind: any, index: number) => {
                  const faltam = (ind.denominador || 0) - (ind.numerador || 0);
                  const potencial = ((100 - (ind.percentual ?? 0)) * (ind.peso ?? 0)) / 100;
                  const indColor = ind.percentual >= 75 ? '#2e7d32' : ind.percentual >= 50 ? '#1565c0' : ind.percentual >= 25 ? '#e65100' : '#c62828';
                  return (
                    <Grid item xs={12} md={indicators.length >= 5 ? 6 : 12} key={ind.indicador || index}>
                      <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                        <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{ind.indicador} - {ind.nome}</Typography>
                                <HelpButton eixo={eixo!} indicador={ind.indicador} size={14} />
                              </Box>
                              {faltam > 0 && (
                                <Typography variant="caption" sx={{ color: '#e65100' }}>
                                  Faltam {faltam.toLocaleString('pt-BR')} cidadaos
                                  {potencial > 2 && <Typography component="span" sx={{ color: '#1565c0', fontWeight: 600 }}> (+{potencial.toFixed(1)} pts)</Typography>}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                              <Typography variant="caption" color="text.secondary">Peso {ind.peso}</Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: indColor, minWidth: 36, textAlign: 'right' }}>
                                {ind.pontuacao} pts
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1 }}>
                              <ProgressBarWithMetas value={ind.percentual ?? 0} color={indColor} height={8} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 42, textAlign: 'right' }}>{ind.percentual}%</Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {ind.numerador?.toLocaleString('pt-BR')} / {ind.denominador?.toLocaleString('pt-BR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {/* Radar chart */}
            {radarData.length >= 3 && radarData.some((d: any) => d.percentual > 0) && (
              <Grid item xs={12} md={5}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, position: 'sticky', top: 80 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Radar de Indicadores</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={radarData} outerRadius={100}>
                        <PolarGrid stroke={alpha(theme.palette.text.primary, 0.1)} />
                        {/* @ts-ignore recharts type compatibility */}
                        <PolarAngleAxis dataKey="indicador" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <Radar dataKey="percentual" stroke={theme.palette.primary.main} fill={alpha(theme.palette.primary.main, 0.2)} fillOpacity={0.6} />
                        <Tooltip formatter={(value: any) => [`${value}%`, 'Cumprimento']} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 1: Por Equipe */}
        <TabPanel value={tabValue} index={1}>
          {/* Search */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar equipe..."
              value={equipeSearch}
              onChange={(e) => setEquipeSearch(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Typography variant="body2" color="text.secondary">
              {sortedEquipes.length} equipes ordenadas por score (pior primeiro)
            </Typography>
          </Box>

          {/* EquipeCards */}
          {sortedEquipes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nenhuma equipe encontrada.</Typography>
          ) : (
            sortedEquipes.map((eq: any) => (
              <EquipeCard
                key={eq.ine}
                equipe={eq}
                indicadoresNomes={indicadoresNomes}
                mediaIndicadores={mediaIndicadores}
                onVerPacientes={(ine) => navigate(`/saude/${eixo}/lista?ine=${ine}`)}
              />
            ))
          )}
        </TabPanel>

        {/* Tab 2: Lista Nominal (inline) */}
        {!isTaxa && (
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/saude/${eixo}/lista`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4 }}
              >
                Abrir Lista Nominal Completa
              </Button>
            </Box>
          </TabPanel>
        )}
      </Box>
    </Fade>
    {municipioAtivo && dadosContexto && (
      <AnalisePredicaoCard
        dadosContexto={dadosContexto}
        municipioId={municipioAtivo.id}
        painelId={`eixo-${eixo}`}
        quadrimestre={quadrimestre}
        ano={ano}
      />
    )}
    <ChatIAButton contexto="saude" eixo={eixo} dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </>
  );
};

export default SaudeEixo;
